import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  ConfirmDeleteDialogComponent,
  ConfirmDeleteDialogData,
} from '../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';

/** Width shared with every other admin confirm-delete dialog. */
const CONFIRM_DIALOG_WIDTH = '500px';

/**
 * Canonical delete UX for admin detail pages (founder decision 2026-07-19):
 * a clearly separated "danger zone" section at the BOTTOM of the page —
 * plain-language consequence copy plus a Delete button that is confirm-dialog
 * gated. Never a header delete button (R-BTN-01: Edit and Delete must not be
 * peers). Deletes are soft-delete semantics server-side (`deletedAt`).
 *
 * Renders the shared `.adm-danger-zone` chrome (single source of truth in
 * `libs/front/shared/design-tokens/src/_admin-tables.scss`) and opens the
 * shared parameterized {@link ConfirmDeleteDialogComponent}. `(confirmed)`
 * fires only after the admin confirms in the dialog — the consumer owns the
 * actual delete call, busy state and navigation:
 *
 * ```html
 * <bodyanalytics-adm-danger-zone
 *   label="Delete this customer"
 *   [description]="'Permanently removes ' + name + ' and their records. This can\'t be undone.'"
 *   buttonText="Delete customer"
 *   [busy]="isDeleting()"
 *   (confirmed)="deleteCustomer()" />
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-adm-danger-zone',
  standalone: true,
  templateUrl: './adm-danger-zone.component.html',
  styleUrls: ['./adm-danger-zone.component.scss'],
})
export class AdmDangerZoneComponent {
  /** Section title; the template falls back to its default copy when unset. */
  readonly label = input<string | undefined>(undefined);
  /** Consequence copy shown under the title. */
  readonly description = input<string | undefined>(undefined);
  /** Delete button label. */
  readonly buttonText = input<string | undefined>(undefined);
  /** Disables the button and swaps in the in-progress label while deleting. */
  readonly busy = input<boolean>(false);

  /** Confirm-dialog heading; unset falls through to the dialog's default. */
  readonly confirmTitle = input<string | undefined>(undefined);
  /** Confirm-dialog body copy; unset falls through to the dialog's default. */
  readonly confirmContent = input<string | undefined>(undefined);
  /** Confirm-dialog destructive button label; unset uses the dialog default. */
  readonly confirmButtonText = input<string | undefined>(undefined);

  /** The admin confirmed the destructive action in the dialog. */
  readonly confirmed = output<void>();

  private readonly dialog = inject(MatDialog);

  protected onDeleteClick(): void {
    const data: ConfirmDeleteDialogData = {
      header: this.confirmTitle(),
      content: this.confirmContent(),
      confirmLabel: this.confirmButtonText(),
    };
    this.dialog
      .open<ConfirmDeleteDialogComponent, ConfirmDeleteDialogData, boolean>(ConfirmDeleteDialogComponent, {
        width: CONFIRM_DIALOG_WIDTH,
        data,
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.confirmed.emit();
        }
      });
  }
}
